import { graphql } from '@/__generated__';
import { SortDirection } from '@/__generated__/graphql';
import { FileSortField } from '@/__generated__/graphql';
import { useLazyQuery } from '@/hooks/useLazyQuery';

export const GetProjectFiles = graphql(`
  query ProjectFiles(
    $environmentId: BigInt!
    $first: Int
    $filter: FileFilter
    $after: String
    $sort: FileSort
  ) {
    environment(id: $environmentId) {
      applied {
        files(first: $first, filter: $filter, after: $after, sort: $sort) {
          totalCount
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              uniqueId
              fileType
              hasChildren
              projectId
              name
              filePath
              environmentId
              resourceType
            }
          }
        }
      }
    }
  }
`);

type ProjectFilesArgs = {
  environmentId: number;
  path: string;
  uniqueId: string;
  after?: string;
};

export const useLazyProjectFiles = () => {
  const [catalogEntity, queryExecute] = useLazyQuery(GetProjectFiles);

  const executeQuery = async (args: ProjectFilesArgs) => {
    const res = await queryExecute({
      environmentId: args.environmentId ?? 0,
      first: 10,
      after: args.after ?? '',
      sort: {
        field: FileSortField.FileType,
        direction: SortDirection.Asc,
      },
      filter: {
        path: args.path ?? '',
        uniqueId: args.uniqueId ?? '',
        resourceTypes: [],
      },
    });
    return {
      ...res,
      data: res.data?.environment.applied?.files.edges.map((edge) => edge.node),
      fetching: false,
      pageInfo: res.data?.environment.applied?.files.pageInfo,
    };
  };

  return [
    {
      ...catalogEntity,
      data: catalogEntity.data?.environment.applied?.files.edges.map(
        (edge) => edge.node,
      ),
      pageInfo: catalogEntity.data?.environment.applied?.files.pageInfo,
    },
    executeQuery,
  ] as const;
};

export const useLazyProjectFile = () => {
  const [catalogEntity, queryExecute] = useLazyQuery(GetProjectFiles);

  const executeQuery = async (args: Omit<ProjectFilesArgs, 'path'>) => {
    const res = await queryExecute({
      environmentId: args.environmentId ?? 0,
      first: 10,
      after: args.after ?? '',
      sort: {
        field: FileSortField.FileType,
        direction: SortDirection.Asc,
      },
      filter: {
        path: null,
        uniqueId: args.uniqueId ?? '',
        resourceTypes: [],
      },
    });
    return {
      ...res,
      data: res.data?.environment.applied?.files.edges.map((edge) => edge.node)[0],
      fetching: false,
      pageInfo: res.data?.environment.applied?.files.pageInfo,
    };
  };

  return [
    {
      ...catalogEntity,
      data: catalogEntity.data?.environment.applied?.files.edges.map(
        (edge) => edge.node,
      )[0],
      pageInfo: catalogEntity.data?.environment.applied?.files.pageInfo,
    },
    executeQuery,
  ] as const;
};
